import { useEffect, useRef, useState } from "react";
import { AdminRow, ApiError, DirectoryUser, api } from "../api/client";
import { useToast } from "../ui/toast";
import { IconSearch, IconRefresh, IconX } from "../ui/icons";

/**
 * Administratorzy — who may access the license console. Pick users from the SOFTRONIC
 * Entra directory (Graph people-picker) and grant/revoke access. The built-in owner
 * (env break-glass) is shown but cannot be removed, so no one can lock everyone out.
 */
export function Admins() {
  const toast = useToast();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyOid, setBusyOid] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { admins } = await api.listAdmins();
      setAdmins(admins);
      // Resolve display names for built-in owners (backend returns them without a name).
      const toResolve = admins.filter((a) => a.builtin && !a.displayName);
      if (toResolve.length) {
        const resolved = await Promise.all(
          toResolve.map((a) =>
            api.getDirectoryUser(a.oid).then(
              (u) => ({ oid: a.oid, name: u.displayName, mail: u.mail ?? u.userPrincipalName }),
              () => null,
            ),
          ),
        );
        setAdmins((prev) =>
          prev.map((a) => {
            const r = resolved.find((x) => x && x.oid === a.oid);
            return r ? { ...a, displayName: r.name, email: a.email || r.mail } : a;
          }),
        );
      }
    } catch (e) {
      toast((e as ApiError).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (u: DirectoryUser) => {
    if (admins.some((a) => a.oid === u.id)) {
      toast("Ten użytkownik już ma dostęp.");
      return;
    }
    setBusyOid(u.id);
    try {
      await api.addAdmin({
        oid: u.id,
        displayName: u.displayName,
        email: u.mail ?? u.userPrincipalName,
      });
      toast(`Dodano: ${u.displayName}`);
      await load();
    } catch (e) {
      toast((e as ApiError).message);
    } finally {
      setBusyOid(null);
    }
  };

  const remove = async (a: AdminRow) => {
    setBusyOid(a.oid);
    try {
      await api.removeAdmin(a.oid);
      toast(`Usunięto dostęp: ${a.displayName || a.oid}`);
      await load();
    } catch (e) {
      toast((e as ApiError).message);
    } finally {
      setBusyOid(null);
    }
  };

  return (
    <>
      <div className="section-hint" style={{ marginBottom: 14 }}>
        Zdefiniuj, kto ma dostęp do konsoli licencji. Wyszukaj osobę w katalogu Entra (SOFTRONIC)
        i nadaj jej dostęp. Każdy administrator może dodawać i usuwać innych.
      </div>

      <PeoplePicker onPick={add} disabled={busyOid !== null} />

      <div className="card" style={{ padding: 8, marginTop: 14, maxWidth: 720 }}>
        <div className="row" style={{ justifyContent: "space-between", padding: "8px 12px" }}>
          <div className="section-title">Administratorzy ({admins.length})</div>
          <button className="btn icon sm ghost" onClick={load} title="Odśwież">
            <IconRefresh />
          </button>
        </div>
        {loading ? (
          <div className="empty">Wczytywanie…</div>
        ) : admins.length === 0 ? (
          <div className="empty">Brak administratorów.</div>
        ) : (
          admins.map((a) => (
            <div
              key={a.oid}
              className="row"
              style={{ justifyContent: "space-between", padding: "10px 12px", borderTop: "1px solid var(--line)" }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{a.displayName || a.oid}</div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {a.email || "—"}
                  {a.builtin && (
                    <span className="badge st-issued" style={{ marginLeft: 8 }}>
                      wbudowany właściciel
                    </span>
                  )}
                </div>
              </div>
              <button
                className="btn danger sm"
                disabled={!a.removable || busyOid === a.oid}
                title={a.removable ? "Odbierz dostęp" : "Wbudowanego właściciela nie można usunąć"}
                onClick={() => remove(a)}
              >
                <IconX /> Usuń
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function PeoplePicker({ onPick, disabled }: { onPick: (u: DirectoryUser) => void; disabled: boolean }) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const users = await api.searchDirectory(q);
        setResults(users);
        setOpen(true);
      } catch (e) {
        const err = e as ApiError;
        // 403 → admin consent for User.ReadBasic.All not granted yet.
        toast(
          err.status === 403
            ? "Brak zgody na odczyt katalogu. Poproś administratora o zatwierdzenie uprawnień aplikacji."
            : err.message,
        );
        setOpen(false);
      } finally {
        setSearching(false);
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <div className="card" style={{ padding: 16, maxWidth: 720, position: "relative" }}>
      <div className="label">Dodaj administratora</div>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <IconSearch />
        <input
          className="input"
          placeholder="Wpisz imię, nazwisko lub e-mail (min. 2 znaki)…"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          style={{ flex: 1 }}
        />
      </div>
      {open && (
        <div
          className="card"
          style={{ position: "absolute", left: 16, right: 16, top: 76, zIndex: 20, padding: 4, maxHeight: 320, overflowY: "auto" }}
        >
          {searching ? (
            <div className="empty">Szukam…</div>
          ) : results.length === 0 ? (
            <div className="empty">Brak wyników.</div>
          ) : (
            results.map((u) => (
              <button
                key={u.id}
                className="row"
                style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: "none", border: "none", cursor: "pointer" }}
                onClick={() => {
                  onPick(u);
                  setQuery("");
                  setOpen(false);
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{u.displayName}</div>
                  <div className="faint" style={{ fontSize: 12 }}>{u.mail ?? u.userPrincipalName}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
